/**
 * <bundle-builder> — sections/bundle-builder.liquid
 */
(function () {
  if (customElements.get("bundle-builder")) return;

  const MOBILE_QUERY = "(max-width: 749px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = format || "${{amount}}";

    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split(".");
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };

    const match = template.match(pattern);
    let formatted;

    switch (match ? match[1] : "amount") {
      case "amount_no_decimals":
        formatted = withDelimiters(value, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formatted = withDelimiters(value, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formatted = withDelimiters(value, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formatted = withDelimiters(value, 2, "'", ".");
        break;
      case "amount_with_space_separator":
        formatted = withDelimiters(value, 2, " ", ",");
        break;
      default:
        formatted = withDelimiters(value, 2, ",", ".");
    }

    return template.replace(pattern, formatted);
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.size = Number(this.dataset.size) || 4;
      this.min = Math.min(Number(this.dataset.min) || this.size, this.size);
      this.moneyFormat = this.dataset.moneyFormat;
      this.items = [];
      this.uid = 0;

      this.slotsEl = this.querySelector("[data-bnd-slots]");
      this.countEl = this.querySelector("[data-bnd-count]");
      this.subtotalEl = this.querySelector("[data-bnd-subtotal]");
      this.statusEl = this.querySelector("[data-bnd-status]");
      this.submitBtn = this.querySelector("[data-bnd-submit]");
      this.track = this.querySelector("[data-bnd-track]");
      this.emptySlotHtml = this.slotsEl?.querySelector("[data-bnd-slot]")?.innerHTML || "";

      this.cards = Array.from(this.querySelectorAll("[data-bpc]")).map((el) => this.setupCard(el));

      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      this.onTrackKeydown = this.onTrackKeydown.bind(this);
      this.syncTrack = this.syncTrack.bind(this);

      document.addEventListener("pointerdown", this.onDocumentPointer);
      this.submitBtn?.addEventListener("click", () => this.submit());
      this.slotsEl?.addEventListener("click", (event) => {
        const remove = event.target.closest("[data-bnd-remove]");
        if (remove) this.remove(Number(remove.dataset.uid));
      });

      if (this.track) {
        this.track.addEventListener("keydown", this.onTrackKeydown);
        this.mobileQuery.addEventListener("change", this.syncTrack);
        this.syncTrack();
      }

      this.render({ announce: false });
    }

    disconnectedCallback() {
      document.removeEventListener("pointerdown", this.onDocumentPointer);
      if (this.mobileQuery) this.mobileQuery.removeEventListener("change", this.syncTrack);
    }

    label(key, replacements = {}) {
      let text = this.dataset[`label${key}`] || "";
      Object.entries(replacements).forEach(([name, value]) => {
        text = text.split(`[${name}]`).join(value);
      });
      return text;
    }

    /* ---------- cards ---------- */

    setupCard(el) {
      let variants = [];
      try {
        variants = JSON.parse(el.querySelector("[data-bpc-variants]")?.textContent || "[]");
      } catch (error) {
        variants = [];
      }

      const card = {
        el,
        title: el.dataset.productTitle || "",
        variants,
        addBtn: el.querySelector("[data-bpc-add]"),
        picker: el.querySelector("[data-bpc-picker]"),
        selects: Array.from(el.querySelectorAll("[data-bpc-option]")),
        confirmBtn: el.querySelector("[data-bpc-confirm]"),
        statusEl: el.querySelector("[data-bpc-status]"),
      };

      card.addBtn?.addEventListener("click", () => {
        if (card.addBtn.getAttribute("aria-disabled") === "true") {
          this.announce(this.label("Full"));
          return;
        }

        if (card.picker) {
          this.togglePicker(card);
        } else if (card.variants[0]) {
          this.add(card, card.variants[0], card.addBtn);
        }
      });

      el.addEventListener("click", (event) => {
        const swatch = event.target.closest(".pc_swatch, .pc_swatch_label");
        if (!swatch || !el.contains(swatch)) return;

        event.preventDefault();
        if (swatch.classList.contains("disabled")) return;
        this.selectSwatch(card, swatch);
      });

      if (card.picker) {
        card.selects.forEach((select) => {
          Array.from(select.options).forEach((option) => {
            option.dataset.label = option.textContent.trim();
          });
          select.addEventListener("change", () => this.syncPicker(card));
        });
        card.confirmBtn?.addEventListener("click", () => {
          const variant = this.selectedVariant(card);
          if (!variant || !variant.available) return;
          if (this.add(card, variant, card.addBtn)) this.closePicker(card, true);
        });
        card.picker.querySelector("[data-bpc-close]")?.addEventListener("click", () => this.closePicker(card, true));
        card.picker.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            this.closePicker(card, true);
          }
        });
        card.picker.addEventListener("focusout", (event) => {
          const next = event.relatedTarget;
          if (!next || card.picker.contains(next) || card.addBtn.contains(next)) return;
          this.closePicker(card, false);
        });
      }

      return card;
    }

    selectSwatch(card, swatch) {
      card.el.querySelectorAll(".pc_swatch, .pc_swatch_label").forEach((item) => {
        item.classList.toggle("pc_swatch--active", item === swatch);
      });

      const image = swatch.dataset.image;
      const mainImage = card.el.querySelector(".main_pc_image");
      if (image && mainImage) {
        mainImage.removeAttribute("srcset");
        mainImage.src = image;
      }

      const matched = this.preselectColor(card, swatch.getAttribute("title"));
      if (!matched || !card.picker || !card.addBtn) return;
      if (card.addBtn.disabled) return;

      if (card.addBtn.getAttribute("aria-disabled") === "true") {
        this.announce(this.label("Full"));
        return;
      }

      if (card.picker.hidden) {
        this.openPicker(card);
      } else {
        this.syncPicker(card);
      }
    }

    preselectColor(card, value) {
      if (!value || !card.selects.length) return false;

      const colorNames = ["color", "colors", "colour", "colours", "couleur", "couleurs", "farbe", "farben", "colore", "colori"];
      const select =
        card.selects.find((item) => colorNames.includes(item.dataset.optionName)) || null;
      if (!select) return false;

      const match = Array.from(select.options).find((option) => option.value === value);
      if (!match) return false;

      select.value = value;
      if (card.picker && !card.picker.hidden) this.syncPicker(card);
      return true;
    }

    markOptionAvailability(card) {
      const values = card.selects.map((select) => select.value);

      card.selects.forEach((select, selectIndex) => {
        Array.from(select.options).forEach((option) => {
          const available = card.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, index) =>
                index === selectIndex ? value === option.value : value === values[index],
              ),
          );
          const base = option.dataset.label || option.textContent.trim();
          option.textContent = available ? base : `${base} – ${this.label("SoldOut")}`;
        });
      });
    }

    selectedVariant(card) {
      const values = card.selects.map((select) => select.value);
      return card.variants.find((variant) =>
        variant.options.every((value, index) => value === values[index]),
      );
    }

    syncPicker(card) {
      const variant = this.selectedVariant(card);
      let message = "";
      let disabled = false;

      if (!variant) {
        message = this.label("Unavailable");
        disabled = true;
      } else if (!variant.available) {
        message = this.label("SoldOut");
        disabled = true;
      } else if (this.countOf(variant.id) >= variant.max) {
        message = this.label("Stock", { item: card.title });
        disabled = true;
      }

      this.markOptionAvailability(card);

      if (card.statusEl) card.statusEl.textContent = message;
      if (card.confirmBtn) {
        card.confirmBtn.disabled = disabled;
        card.confirmBtn.classList.toggle("btn--disabled", disabled);
      }
    }

    togglePicker(card) {
      if (card.picker.hidden) {
        this.openPicker(card);
      } else {
        this.closePicker(card, true);
      }
    }

    openPicker(card) {
      this.cards.forEach((other) => {
        if (other !== card && other.picker && !other.picker.hidden) this.closePicker(other, false);
      });

      card.picker.hidden = false;
      card.addBtn.setAttribute("aria-expanded", "true");
      card.el.classList.add("bpc--picking");
      this.syncPicker(card);

      const first = card.selects[0] || card.confirmBtn;
      first?.focus();
    }

    closePicker(card, restoreFocus) {
      if (!card.picker || card.picker.hidden) return;

      card.picker.hidden = true;
      card.addBtn.setAttribute("aria-expanded", "false");
      card.el.classList.remove("bpc--picking");
      if (restoreFocus) card.addBtn.focus();
    }

    onDocumentPointer(event) {
      this.cards.forEach((card) => {
        if (!card.picker || card.picker.hidden) return;
        if (card.picker.contains(event.target) || card.addBtn.contains(event.target)) return;
        this.closePicker(card, false);
      });
    }

    /* ---------- bundle state ---------- */

    countOf(variantId) {
      return this.items.filter((item) => item.variantId === variantId).length;
    }

    add(card, variant, trigger) {
      const itemName = variant.options.length ? `${card.title} – ${variant.title}` : card.title;

      if (this.items.length >= this.size) {
        this.announce(this.label("Full"), true);
        return false;
      }

      if (!variant.available) {
        this.announce(`${itemName}: ${this.label("SoldOut")}`, true);
        return false;
      }

      if (this.countOf(variant.id) >= variant.max) {
        this.announce(this.label("Stock", { item: itemName }), true);
        return false;
      }

      this.uid += 1;
      this.items.push({
        uid: this.uid,
        variantId: variant.id,
        name: itemName,
        price: Number(variant.price) || 0,
        image: variant.image,
      });

      this.render({ message: this.label("Added", { item: itemName }) });
      if (trigger && !trigger.disabled && trigger.getAttribute("aria-disabled") !== "true") trigger.focus();
      return true;
    }

    remove(uid) {
      const index = this.items.findIndex((item) => item.uid === uid);
      if (index < 0) return;

      const [removed] = this.items.splice(index, 1);
      this.render({ message: this.label("Removed", { item: removed.name }) });

      const buttons = Array.from(this.slotsEl.querySelectorAll("[data-bnd-remove]"));
      const next = buttons[Math.min(index, buttons.length - 1)];
      if (next) {
        next.focus();
      } else {
        this.slotsEl.setAttribute("tabindex", "-1");
        this.slotsEl.focus();
      }
    }

    render({ message = "", announce = true } = {}) {
      this.renderSlots();

      const count = this.items.length;
      const total = this.items.reduce((sum, item) => sum + item.price, 0);
      const ready = count >= this.min && count > 0;
      const full = count >= this.size;

      if (this.countEl) this.countEl.textContent = `(${count}/${this.size})`;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(total, this.moneyFormat);

      if (this.submitBtn) {
        this.submitBtn.disabled = !ready;
        this.submitBtn.classList.toggle("btn--disabled", !ready);
      }

      this.cards.forEach((card) => {
        if (!card.addBtn || card.addBtn.disabled) return;
        if (full) {
          card.addBtn.setAttribute("aria-disabled", "true");
        } else {
          card.addBtn.removeAttribute("aria-disabled");
        }
        if (card.picker && !card.picker.hidden) this.syncPicker(card);
      });

      const progress = ready
        ? this.label("Ready")
        : this.label("More", { count: String(Math.max(this.min - count, 0)) });

      const text = [message, progress].filter(Boolean).join(" ");
      if (announce) {
        this.announce(text);
      } else if (this.statusEl) {
        this.statusEl.classList.remove("bnd_status--error");
        this.statusEl.textContent = count ? text : "";
      }
    }

    renderSlots() {
      if (!this.slotsEl) return;

      const slots = Array.from(this.slotsEl.querySelectorAll("[data-bnd-slot]"));

      slots.forEach((slot, index) => {
        const item = this.items[index];
        slot.classList.toggle("is-filled", Boolean(item));

        if (!item) {
          slot.innerHTML = this.emptySlotHtml;
          return;
        }

        slot.innerHTML = "";

        const media = document.createElement("span");
        media.className = "bnd_slot_media";
        if (item.image) {
          const img = document.createElement("img");
          img.src = item.image;
          img.alt = "";
          img.width = 150;
          img.height = 150;
          img.loading = "lazy";
          media.appendChild(img);
        }

        const name = document.createElement("span");
        name.className = "visually-hidden";
        name.textContent = item.name;

        const button = document.createElement("button");
        button.type = "button";
        button.className = "bnd_slot_remove";
        button.dataset.bndRemove = "";
        button.dataset.uid = String(item.uid);
        button.setAttribute("aria-label", this.label("Remove", { item: item.name }));
        button.innerHTML =
          '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

        slot.append(media, name, button);
      });
    }

    announce(text, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.classList.toggle("bnd_status--error", isError);
      this.statusEl.textContent = "";
      window.requestAnimationFrame(() => {
        this.statusEl.textContent = text;
      });
    }

    /* ---------- cart ---------- */

    async submit() {
      if (this.isSubmitting || this.items.length < this.min || this.items.length === 0) return;

      this.isSubmitting = true;
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute("aria-busy", "true");
      this.submitBtn.classList.add("is-loading");

      const bundleId = `Bundle-${Date.now().toString(36)}`;
      const grouped = new Map();
      this.items.forEach((item) => {
        grouped.set(item.variantId, (grouped.get(item.variantId) || 0) + 1);
      });

      const payload = {
        items: Array.from(grouped, ([id, quantity]) => ({
          id,
          quantity,
          properties: { _bundle: bundleId },
        })),
      };

      const root = window.Shopify?.routes?.root || "/";

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw data;

        this.items = [];
        this.render({ announce: false });
        if (this.statusEl) this.statusEl.textContent = "";

        await this.openCart(root);
      } catch (error) {
        this.announce(error?.description || error?.message || this.label("Error"), true);
      } finally {
        this.isSubmitting = false;
        this.submitBtn.removeAttribute("aria-busy");
        this.submitBtn.classList.remove("is-loading");

        const ready = this.items.length >= this.min && this.items.length > 0;
        this.submitBtn.disabled = !ready;
        this.submitBtn.classList.toggle("btn--disabled", !ready);
      }
    }

    async openCart(root) {
      const drawer = document.querySelector(".cart_drawer_wrapper");
      if (!drawer) {
        window.location.href = `${root}cart`;
        return;
      }

      try {
        const sections = await fetch(`${root}?sections=cart-drawer,cart,cart-bubble`).then((r) => r.json());
        document.dispatchEvent(new CustomEvent("cart-updated", { detail: { sections }, bubbles: true }));
      } catch (error) {
        window.location.href = `${root}cart`;
        return;
      }

      const open = typeof window.openDrawer === "function" ? window.openDrawer : null;
      const lock = typeof window.AddScrollLock === "function" ? window.AddScrollLock : null;

      if (open) {
        open(drawer, this.submitBtn);
        if (lock) lock();
      }

      window.requestAnimationFrame(() => {
        if (drawer.classList.contains("active")) return;

        const toggle = drawer.id ? document.querySelector(`[data-drawer="${drawer.id}"]`) : null;
        if (toggle) {
          toggle.click();
        } else {
          window.location.href = `${root}cart`;
        }
      });
    }

    /* ---------- mobile slider ---------- */

    syncTrack() {
      if (this.mobileQuery.matches) {
        this.track.setAttribute("tabindex", "0");
      } else {
        this.track.removeAttribute("tabindex");
      }
    }

    onTrackKeydown(event) {
      if (!this.mobileQuery.matches || event.target !== this.track) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;

      event.preventDefault();
      const first = this.track.querySelector(".bnd_item");
      const step = first ? first.getBoundingClientRect().width + 12 : 200;
      this.track.scrollBy({
        left: event.key === "ArrowRight" ? step : -step,
        behavior: this.reducedMotionQuery.matches ? "auto" : "smooth",
      });
    }
  }

  customElements.define("bundle-builder", BundleBuilder);
})();
